import { Component, QueryList, ViewChildren } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import {
  ConseilConfig,
  ConseilConfigPaginatedCollectionSegment,
  FicheConseil,
  FondsEvenementiel,
  FondsEvenementielConseil,
  FrequenceVersement,
  Produit,
  ProduitConseil,
  ProduitSpecificConfig,
  VpModeOptions,
} from '@lib/models/generated/graphql';
import { NatureOperationId } from '@lib/models/graphqlData';
import { AuthService } from '@lib/services/auth-service.service';
import { gql } from 'apollo-angular';
import { ApplicationInsightsService, QueryManagerService } from 'finzzle-angular-utils';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { OuiButtonToggleOption } from 'omnium-ui/button-toggle';
import { OuiAutocompleteOption, OuiSelectOption, OuiValidationError } from 'omnium-ui/form-field';
import { distinctUntilChanged, lastValueFrom, Subscription } from 'rxjs';
import { AbstractStepComponent } from 'src/app/components/declare-fiche-conseil/steps/abstract-step.component';
import { AllocationAssetComponent } from 'src/app/components/declare-fiche-conseil/steps/projet-investissement-step/allocation-asset/allocation-asset.component';
import { ModeDeGestionComponent } from 'src/app/components/declare-fiche-conseil/steps/projet-investissement-step/mode-de-gestion/mode-de-gestion.component';
import {
  getProjetInvestissementUiPolicy,
  ProjetInvestissementUiPolicy,
} from 'src/app/components/declare-fiche-conseil/steps/projet-investissement-step/projet-investissement-ui-policy';
import { VersementComponent } from 'src/app/components/declare-fiche-conseil/steps/projet-investissement-step/versement/versement.component';
import { FicheConseilService } from 'src/app/services/fiche-conseil.service';
import { FondsEvenementielService } from 'src/app/services/fonds-evenementiel.service';
import { FormConfigService } from 'src/app/services/form-config.service';
import { OptionAutomatiqueService } from 'src/app/services/option-automatique.service';
import { FieldFormControl } from 'src/fieldFormControl/FieldFormControl';
import { environment } from 'src/environments/environment';
export const fullAdequationConfigFragment = gql`
  fragment FullAdequationConfig on AdequationConfig {
    lastChangeDate
    fieldConfigs {
      id
      fieldKey
      isVisible
      validators {
        errorMessage
        id
        validatorKey
        parameters {
          id
          key
          value
        }
      }
    }
  }
`;
export const modeDeGestionConfigFragment = gql`
  fragment modeDeGestionConfig on ModeDeGestionConfig {
    id
    libelle
    code
    conditionVersementInitial {
      montantMinSouscription
      montantMinAvecVP
      montantMinAvecTransfert
    }
    conditionVersementComplementaire {
      montantMinVC
      montantMinAvecVP
      montantMinAvecTransfert
    }
    conditionVersementProgramme {
      montantMinAnnuel
      montantMinMensuel
      montantMinSemestriel
      montantMinTrimestriel
    }
  }
`;
const PRODUITS_FROM_CONFIGID = gql`
  query conseilConfigById($id: String!) {
    conseilConfigPaginated(take: 1, skip: 0, where: { id: { eq: $id } }) {
      items {
        configFormulaire {
          id
          enveloppeConfig {
            ...FullAdequationConfig
          }
          produitConfigs {
            gestionPossibles {
              ...modeDeGestionConfig
            }
            produit {
              id
            }
            config {
              ...FullAdequationConfig
            }
          }
        }
      }
    }
  }
  ${fullAdequationConfigFragment},
  ${modeDeGestionConfigFragment}
`;

const PRODUIT_BY_ID = gql`
  query produitById($id: Long!) {
    produitById(id: $id) {
      id
      nom
      nomTechniqueProduitBox
      habilitation
      idPartenaireNavigation {
        id
        nom
      }
    }
  }
`;

const requiredValueError: OuiValidationError = {
  code: 'required',
  message: 'Ce champs est requis !',
};

const arbitrageLibreMotivationOperationOptions: OuiSelectOption<string>[] = [
  {
    label: "Changement de stratégie d'investissement",
    value: 'CHANGEMENT_STRATEGIE_INVESTISSEMENT',
  },
  {
    label: 'Réinvestissement, produits structures, fonds a échéance, coupon, etc.',
    value: 'REINVESTISSEMENT',
  },
  {
    label: 'Diversification du portefeuille',
    value: 'DIVERSIFICATION',
  },
  {
    label: "Opportunité d'investissement",
    value: 'OPPORTUNITE',
  },
  {
    label: 'Sécurisation du contrat',
    value: 'SECURISATION',
  },
  {
    label: 'Dynamisation du contrat',
    value: 'DYNAMISATION',
  },
  {
    label: 'Rééquilibrage du contrat',
    value: 'REEQUILIBRAGE',
  },
];

const changementModeGestionMotivationOperationOptions: OuiSelectOption<string>[] = [
  {
    label: "Changement de stratégie d'investissement",
    value: 'CHANGEMENT_STRATEGIE_INVESTISSEMENT',
  },
  {
    label: "Opportunité d'investissement",
    value: 'OPPORTUNITE',
  },
  {
    label: 'Sécurisation du contrat',
    value: 'SECURISATION',
  },
  {
    label: 'Dynamisation du contrat',
    value: 'DYNAMISATION',
  },
];

const defaultArbitrageLibreModeDeGestion = {
  code: 'LIBRE',
  libelle: 'Libre',
};

type ProduitSpecificConfigWithGestionPossibles = ProduitSpecificConfig & {
  gestionPossibles?: Produit['gestionPossibles'];
};
type FondsEvenementielWithOrderConseil = FondsEvenementielConseil & { ordre: number };

@Component({
  selector: 'app-projet-investissement',
  templateUrl: './projet-investissement.component.html',
  styleUrls: ['./projet-investissement.component.scss'],
})
export class ProjetInvestissementStepComponent extends AbstractStepComponent {
  @ViewChildren(VersementComponent) versementComponents: QueryList<VersementComponent>;
  @ViewChildren(ModeDeGestionComponent) modeDeGestionComponents: QueryList<ModeDeGestionComponent>;
  @ViewChildren(AllocationAssetComponent) allocationAssetComponents: QueryList<AllocationAssetComponent>;

  selectableProduits: ProduitConseil[] = [];
  produitControl: FormControl<number | null>;
  produitOptions: OuiAutocompleteOption<number>[] = [];
  produitErrors: OuiValidationError[] = [requiredValueError];
  motivationOperationControl: FieldFormControl<string | null, string | null>;
  motivationOperationErrors: OuiValidationError[] = [requiredValueError];
  habilSub: Subscription;
  conseilConfig: ConseilConfig | null = null;
  transfertOptions: OuiButtonToggleOption<boolean>[] = [
    {
      value: true,
      label: 'Oui',
    },
    {
      value: false,
      label: 'Non',
    },
  ];
  tranfertTogglecontrol: FieldFormControl<boolean, boolean>;
  viFraisVersementControl: FieldFormControl<number, number>;
  viMontantBrutControl: FieldFormControl<number, number>;
  fraisViHint: string | undefined;

  configSub: Subscription;
  viDerogationSub: Subscription;
  private produitValueChangesSub: Subscription;
  private ficheLoadVersion = 0;
  private hasObservedFiche = false;

  slideVpControl: FieldFormControl<boolean, boolean>;
  vpFrequenceControl: FieldFormControl<FrequenceVersement, FrequenceVersement>;
  vpFrequenceOptions: OuiSelectOption<FrequenceVersement>[] = [];
  vpMontantBrutControl: FieldFormControl<number, number>;
  vpFraisVersementControl: FieldFormControl<number, number>;
  vpFraisVersementHint: string | undefined;
  vpFraisVersementDefaultValue: number | undefined;
  vpFraisVersementErrors: OuiValidationError[] = [];
  vpDerogationSub: Subscription;
  slideVpSub: Subscription;

  isUpdatingProduit: boolean = false;
  produitChanged: boolean = false;
  private missingAtlasProduitIds = new Set<number>();

  avecMiseEnPlaceOuModificationVP: FieldFormControl<boolean, boolean>;
  avecArbitragesProgrammes: FieldFormControl<boolean, boolean>;
  selectedVpMode?: VpModeOptions;
  fondsEvenementielsWithOrder: FondsEvenementielWithOrderConseil[] = [];

  changementAllocation = false;
  constructor(
    ficheConseilService: FicheConseilService,
    protected formConfigService: FormConfigService,
    private queryManager: QueryManagerService,
    private userService: AuthService,
    protected optionAutomatiqueService: OptionAutomatiqueService,
    protected fondsEvenementielService: FondsEvenementielService,
    private snackbarService: OuiSnackbarService,
    private loggerService: ApplicationInsightsService
  ) {
    super(ficheConseilService);
    this.produitControl = new FormControl<number | null>(this.currentFicheConseil?.produit?.id, {
      validators: [Validators.required],
    });
    this.motivationOperationControl = new FieldFormControl<string | null, string | null>({
      formConfigService: this.formConfigService,
      ficheConseilService: this.ficheConseilService,
      dataKey: 'motivationOperation',
      defaultValue: this.currentFicheConseil?.motivationOperation ?? null,
      options: {
        validators: [Validators.required],
      },
    });
    this.avecMiseEnPlaceOuModificationVP = new FieldFormControl<boolean, boolean>({
      formConfigService: this.formConfigService,
      ficheConseilService: this.ficheConseilService,
      dataKey: 'avecMiseEnPlaceOuModificationVP',
      defaultValue: false,
    });

    this.avecArbitragesProgrammes = new FieldFormControl<boolean, boolean>({
      formConfigService: this.formConfigService,
      ficheConseilService: this.ficheConseilService,
      dataKey: 'avecArbitragesProgrammes',
      defaultValue: false,
    });
  }
  public override beforeFicheConseilUpdate(
    newFicheConseil: FicheConseil | null,
    previousFicheConseil: FicheConseil | null
  ): void {
    if (newFicheConseil?.id === previousFicheConseil?.id) {
      return;
    }

    const shouldReload = this.hasObservedFiche;
    this.hasObservedFiche = true;
    const loadVersion = ++this.ficheLoadVersion;
    if (!shouldReload) {
      return;
    }

    this.selectableProduits = [];
    this.produitOptions = [];
    this.conseilConfig = null;
    this.formConfigService.setSelectedProduit(null);
    this.produitControl.setValue(null, { emitEvent: false });

    if (newFicheConseil) {
      Promise.resolve().then(() => {
        if (loadVersion === this.ficheLoadVersion) {
          this.fetchDataAndInit(loadVersion);
        }
      });
    }
  }
  public override isStepValid(): boolean {
    let validity = this.produitControl.errors === null && this.produitControl.value !== null;
    if (this.uiPolicy.showMotivationOperationBlock) {
      validity =
        validity && this.motivationOperationControl.errors === null && this.motivationOperationControl.value !== null;
    }
    if (this.versementComponents.first) {
      validity = validity && this.versementComponents.first.isStepValid();
    }
    if (this.modeDeGestionComponents.first) {
      validity = validity && this.modeDeGestionComponents.first.isStepValid();
    }
    if (this.allocationAssetComponents.first) {
      validity = validity && this.allocationAssetComponents.first.isStepValid();
    }
    return validity;
  }
  public override markAsTouched(): void {
    this.produitControl.markAsTouched();
    this.produitControl.updateValueAndValidity();
    this.motivationOperationControl.markAsTouched();
    this.motivationOperationControl.updateValueAndValidity();
    this.versementComponents.first?.markAsTouched();
    this.modeDeGestionComponents.first?.markAsTouched();
    this.allocationAssetComponents.first?.markAsTouched();
  }
  ngOnChanges(): void {}

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.habilSub) {
      this.habilSub.unsubscribe();
    }
    this.produitValueChangesSub?.unsubscribe();
    this.formConfigService.setSelectedProduit(null);
  }

  override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    await this.fetchDataAndInit();
  }

  async fetchDataAndInit(loadVersion = this.ficheLoadVersion) {
    this.ensureArbitrageLibreGestionLibreMode();
    this.habilSub?.unsubscribe();
    this.habilSub = this.userService.userHabilitationCheck.subscribe(async check => {
      if (check) {
        const ficheId = this.currentFicheConseil?.id;
        await this.getConseilConfigAndSelectableProduits(ficheId);
        if (loadVersion !== this.ficheLoadVersion || ficheId !== this.currentFicheConseil?.id) {
          return;
        }
        this.setProduitOptions();
        setTimeout(async () => {
          if (loadVersion !== this.ficheLoadVersion || ficheId !== this.currentFicheConseil?.id) {
            return;
          }
          let preselectProduitId: number | null = null;

          // we wait the the form is initialized to set the value
          if (this.currentFicheConseil?.produit?.id) {
            preselectProduitId = this.currentFicheConseil?.produit?.id;
            await this.loadAndCheckOptionsAutomatiques(preselectProduitId);
          }
          // after init subscribe on product changes
          this.produitControl.setValue(preselectProduitId ?? null, { emitEvent: false });

          await this.handleProduitChange(preselectProduitId, false);

          if (loadVersion !== this.ficheLoadVersion || ficheId !== this.currentFicheConseil?.id) {
            return;
          }
          this.produitValueChangesSub?.unsubscribe();
          this.produitValueChangesSub = this.produitControl.valueChanges
            .pipe(distinctUntilChanged())
            .subscribe(async value => {
              await this.handleProduitChange(value, true);
            });
        });
      }
    });
  }

  private async loadAndCheckOptionsAutomatiques(preselectProduitId: number | null) {
    await this.optionAutomatiqueService.loadOptionsAutomatiquesByProduit(preselectProduitId);
    await this.fondsEvenementielService.loadFondsEvenementielsByProduit(preselectProduitId);
    this.setFondsEvenementielsWithOrder();
    if (
      this.currentFicheConseil?.natureOperation?.id === NatureOperationId.VersementComplementaire &&
      this.optionAutomatiqueService.optionsAutomatiques.length === 0
    ) {
      this.avecArbitragesProgrammes.disable({ emitEvent: false });
    }
  }

  onVpModeChange(v: VpModeOptions) {
    this.selectedVpMode = v;
  }

  setProduitOptions() {
    this.produitOptions = this.selectableProduits
      ?.map(produit => ({
        label: produit.libelle ?? 'sans nom',
        value: produit.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  /**
   * Handles changes to the selected produit by updating the form configuration.
   * If the produitId is undefined, it resets the form configuration to the enveloppeConfig.
   * Otherwise, it finds the corresponding produit config and merges it with the enveloppeConfig
   * to update the form configuration.
   *
   * @param produitId The ID of the selected produit or undefined if no produit is selected.
   */
  async handleProduitChange(produitId: number | null, isFromUser: boolean): Promise<void> {
    if (!this.conseilConfig?.configFormulaire?.enveloppeConfig) return;
    this.isUpdatingProduit = true;
    let prod: ProduitConseil | null = null;
    let foundConfig: ProduitSpecificConfig | null = null;
    if (produitId) {
      prod = this.selectableProduits.find(produit => produit.id === produitId) ?? null;
      foundConfig =
        this.conseilConfig?.configFormulaire?.produitConfigs?.find(
          configProduit => configProduit.produit?.id === produitId
        ) ?? null;
      if (isFromUser) {
        this.produitChanged = true;
      }
    }
    await this.ficheConseilService.onUpdateProduitOnFicheConseil(
      prod,
      this.conseilConfig?.configFormulaire?.enveloppeConfig,
      foundConfig
    );
    this.setFondsEvenementielsWithOrder();
    this.isUpdatingProduit = false;
  }

  async getConseilConfigAndSelectableProduits(expectedFicheId = this.currentFicheConseil?.id): Promise<void> {
    const conseilConfigId = this.currentFicheConseil?.conseilConfigId;
    const resultFromRequest = await lastValueFrom(
      this.queryManager.query<{ conseilConfigPaginated: ConseilConfigPaginatedCollectionSegment }>({
        query: PRODUITS_FROM_CONFIGID,
        variables: {
          id: conseilConfigId,
        },
      })
    );
    if (expectedFicheId !== this.currentFicheConseil?.id) {
      return;
    }
    if (!resultFromRequest.data?.conseilConfigPaginated?.items?.length) {
      this.conseilConfig = null;
      this.selectableProduits = [];
      return;
    }
    const conseilConfig = resultFromRequest.data.conseilConfigPaginated.items[0];

    // filter selectableProduits
    const configProduits =
      conseilConfig?.configFormulaire?.produitConfigs?.map(configProduit => {
        const gestionPossibles = this.getProduitConfigGestionPossibles(configProduit);

        return configProduit.produit
          ? ({
              ...configProduit.produit,
              gestionPossibles,
            } as Produit)
          : configProduit.produit;
      }) ?? [];
    const enrichedProduits = await this.enrichProduitsFromAtlas(configProduits);
    if (expectedFicheId !== this.currentFicheConseil?.id) {
      return;
    }
    const enrichedProduitsById = new Map(enrichedProduits.map(produit => [produit.id, produit]));
    if (conseilConfig?.configFormulaire?.produitConfigs) {
      this.conseilConfig = {
        ...conseilConfig,
        configFormulaire: {
          ...conseilConfig.configFormulaire,
          produitConfigs: conseilConfig.configFormulaire.produitConfigs.map(configProduit => {
            const gestionPossibles = this.getProduitConfigGestionPossibles(configProduit);
            const enrichedProduit = configProduit.produit
              ? enrichedProduitsById.get(configProduit.produit.id) ?? configProduit.produit
              : configProduit.produit;

            return {
              ...configProduit,
              gestionPossibles,
              produit: enrichedProduit
                ? ({
                    ...enrichedProduit,
                    gestionPossibles,
                  } as Produit)
                : enrichedProduit,
            };
          }),
        },
      };
    }
    this.selectableProduits = enrichedProduits
      .filter(p => !!p && (!p.habilitation || this.userService.getUserValidHabilitationIds().includes(p.habilitation)))
      .map(produit => {
        return {
          id: produit!.id,
          libelle: produit!.nomTechniqueProduitBox ?? produit!.nom ?? 'Produit sans nom',
          partenaireId: produit!.idPartenaireNavigation?.id,
          partenaireNom: produit!.idPartenaireNavigation?.nom,
        } as ProduitConseil;
      });
  }

  private async enrichProduitsFromAtlas(produits: (Produit | null | undefined)[]): Promise<Produit[]> {
    const enrichedProduits = await Promise.all(
      produits
        .filter((produit): produit is Produit => !!produit)
        .map(async produit => {
          const gestionPossibles = produit.gestionPossibles ? [...produit.gestionPossibles] : produit.gestionPossibles;
          const atlasProduit = await this.getAtlasProduitById(produit.id);
          return {
            ...produit,
            ...atlasProduit,
            id: produit.id,
            gestionPossibles,
            nom: atlasProduit?.nom ?? this.getMissingAtlasProduitLabel(produit.id),
          } as Produit;
        })
    );

    return enrichedProduits;
  }

  private async getAtlasProduitById(id: number): Promise<Produit | null> {
    try {
      const result = await lastValueFrom(
        this.queryManager.query<{ produitById: Produit | null }>(
          {
            query: PRODUIT_BY_ID,
            variables: { id },
            fetchPolicy: 'network-only',
            errorPolicy: 'all',
          },
          true
        )
      );

      const produit = result.data?.produitById ?? null;
      if (!produit) {
        this.notifyMissingAtlasProduit(id);
      }

      return produit;
    } catch {
      this.notifyMissingAtlasProduit(id);
      return null;
    }
  }

  private getMissingAtlasProduitLabel(id: number): string {
    return environment.production
      ? `Produit ${id} introuvable dans Atlas - contactez le support`
      : `Produit ${id} absent d'Atlas (possible hors prod)`;
  }

  private notifyMissingAtlasProduit(produitId: number): void {
    if (this.missingAtlasProduitIds.has(produitId)) return;

    this.missingAtlasProduitIds.add(produitId);
    const missingProduitIds = Array.from(this.missingAtlasProduitIds).join(', ');

    if (environment.production) {
      this.loggerService.logEvent('MissingAtlasProduitInProjetInvestissementStep', {
        produitId,
        configId: this.currentFicheConseil?.conseilConfigId,
      });
      this.snackbarService.open(
        `Produits introuvables dans Atlas : ${missingProduitIds}. Merci de contacter le support.`,
        'error',
        7000
      );
    } else {
      this.snackbarService.open(
        `Produits absents d'Atlas : ${missingProduitIds}. En hors prod, cela peut arriver.`,
        'warning',
        7000
      );
    }
  }

  get showVersementsProg(): boolean {
    return this.avecMiseEnPlaceOuModificationVP.value;
  }

  onChangementAllocationReceived(val: boolean): void {
    this.changementAllocation = val;
  }

  get uiPolicy(): ProjetInvestissementUiPolicy {
    return getProjetInvestissementUiPolicy(this.currentFicheConseil?.natureOperation?.id);
  }

  get motivationOperationOptions(): OuiSelectOption<string>[] {
    if (this.currentFicheConseil?.natureOperation?.id === NatureOperationId.ChangementModeGestion) {
      return changementModeGestionMotivationOperationOptions;
    }

    return arbitrageLibreMotivationOperationOptions;
  }

  private getProduitConfigGestionPossibles(configProduit: ProduitSpecificConfig | null | undefined): Produit['gestionPossibles'] {
    return (
      (configProduit as ProduitSpecificConfigWithGestionPossibles | null | undefined)?.gestionPossibles ??
      configProduit?.produit?.gestionPossibles
    );
  }

  private ensureArbitrageLibreGestionLibreMode(): void {
    if (
      this.currentFicheConseil?.natureOperation?.id === NatureOperationId.ArbitrageLibre &&
      !this.currentFicheConseil?.modeDeGestion?.code
    ) {
      this.ficheConseilService.onFicheConseilDataEdit('modeDeGestion', defaultArbitrageLibreModeDeGestion);
    }
  }

  protected get shouldShowFondsEvenementielsBlock(): boolean {
    const modeGestionCode = this.currentFicheConseil?.modeDeGestion?.code;
    return (
      modeGestionCode === 'LIBRE' &&
      this.fondsEvenementielService.getFondsEvenementielsByModeGestion(modeGestionCode).length > 0 &&
      (this.currentFicheConseil?.fondsEvenementiels ?? []).length > 0
    );
  }

  private setFondsEvenementielsWithOrder(): void {
    const modeGestionCode = this.currentFicheConseil?.modeDeGestion?.code;
    const ficheFonds = this.currentFicheConseil?.fondsEvenementiels ?? [];
    const fondsFromConfig = modeGestionCode
      ? this.fondsEvenementielService.getFondsEvenementielsByModeGestion(modeGestionCode)
      : [];
    const areFondsDifferent = !this.compareIds(ficheFonds, fondsFromConfig);
    if (areFondsDifferent) {
      const fondsConseils: FondsEvenementielConseil[] =
        fondsFromConfig?.map(
          fonds => {
            const currentFonds = ficheFonds.find(
              ficheFond => ficheFond.id === fonds.id || ficheFond.code === fonds.code
            );
            return {
              id: fonds.id,
              code: fonds.code,
              libelle: fonds.libelle,
              isSelected: currentFonds?.isSelected ?? false,
            } as FondsEvenementielConseil;
          }
        ) ?? [];

      this.ficheConseilService.onFicheConseilDataEdit('fondsEvenementiels', fondsConseils);
    }
    const currentFonds = this.currentFicheConseil?.fondsEvenementiels ?? [];
    this.fondsEvenementielsWithOrder = this.mapFicheFondsToFondsWithOrder(currentFonds, fondsFromConfig);
  }

  private mapFicheFondsToFondsWithOrder(
    ficheFonds: FondsEvenementielConseil[],
    fondsFromConfig: FondsEvenementiel[]
  ): FondsEvenementielWithOrderConseil[] {
    return ficheFonds.map(fonds => {
      const found = fondsFromConfig.find(fondsWithOrder => fondsWithOrder.id === fonds.id);
      if (found) {
        return {
          ...fonds,
          ordre: found.ordre,
        };
      }
      return {
        ...fonds,
        ordre: 9999999,
      };
    });
  }

  private compareIds(listA: { id: string }[], listB: { id: string }[]): boolean {
    const ids1 = new Set(listA.map(item => item.id));
    const ids2 = new Set(listB.map(item => item.id));
    return ids1.size === ids2.size && [...ids1].every(id => ids2.has(id));
  }
}
